import { FC, Fragment, useEffect, useState } from 'react';
import { Col, FormInstance, Grid, Image, Row } from 'antd';
import { useApp } from '@/App';
import FormBuilder, { useFormBuilder } from '@/components/FormBuilder';
import formRule from '@/libs/antd/rules';
import fleetApi from '@/libs/fleet-api/fetcher';
import { AuthPayload, authenticate } from '@/libs/fleet-api/services/users';
import ForgotPasswordModal, {
	useForgotPasswordModal,
} from './ForgotPasswordModal';
import loginBg from '@/assets/login-bg.jpg';

type LoginForm = AuthPayload & {
	rememberMe?: boolean;
};

const LoginPage: FC = () => {
	const screen = Grid.useBreakpoint();
	const { navigate, notification, queryClient } = useApp();
	const { form } = useFormBuilder<LoginForm>();
	const { setFieldsValue } = form;
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		const storedLoginForm = localStorage.getItem('rememberMe');
		if (storedLoginForm) {
			const data = JSON.parse(storedLoginForm) as LoginForm;
			setFieldsValue(data);
		}
	}, [setFieldsValue]);

	const handleSubmit = async (payload: LoginForm) => {
		const { userName, password, rememberMe } = payload;
		if (rememberMe) {
			localStorage.setItem(
				'rememberMe',
				JSON.stringify({ userName, password, rememberMe }),
			);
		} else {
			localStorage.removeItem('rememberMe');
		}

		setIsLoading(true);
		try {
			await authenticate({ userName, password });
			await queryClient.invalidateQueries();
			navigate('DashboardPage');
		} catch (error) {
			if (fleetApi.isError(error)) {
				const message = await error.json();
				notification.error({
					duration: 20,
					message: `Error ID: ${message?.metadata?.debugInfo?.errorId ?? 'Unknown'}`,
					description: (
						<div>
							{message?.metadata?.debugInfo?.messages?.length ? (
								<ul>
									{message?.metadata?.debugInfo?.messages.map((msg: string) => (
										<li key={msg}>{msg}</li>
									))}
								</ul>
							) : (
								<pre>Opps, Something went wrong</pre>
							)}
						</div>
					),
				});
				// notification.error({
				// 	message: 'Oops! something went wrong',
				// 	description: (
				// 		<pre>{JSON.stringify(JSON.parse(message), null, 2)}</pre>
				// 	),
				// });
			}
		}
		setIsLoading(false);
	};

	const [forgotPasswordModal, setForgotPasswordModal] =
		useForgotPasswordModal();

	return (
		<Fragment>
			<div style={{ padding: screen.md ? 76 : 38 }}>
				<Row
					gutter={[76, 38]}
					align={'middle'}
				>
					<Col
						xs={{ span: 24 }}
						md={{ span: 12 }}
					>
						<Image
							src={loginBg}
							preview={false}
						/>
					</Col>
					<Col
						xs={{ span: 24 }}
						md={{ span: 12 }}
					>
						<FormBuilder
							form={form as FormInstance<unknown>}
							config={{
								variant: 'filled',
							}}
							onFinish={handleSubmit}
							fields={[
								{
									name: 'userName',
									label: 'Username',
									type: 'text',
									config: {
										placeholder: 'Insert username',
										size: 'large',
									},
									rules: [formRule.required('Username Wajib Diisi')],
								},
								{
									name: 'password',
									label: 'Password',
									type: 'password',
									config: {
										placeholder: 'Insert password',
										size: 'large',
									},
									rules: [formRule.required('Password Wajib Diisi')],
								},
								{
									type: 'button',
									width: '1/2',
									config: {
										children: 'Forgot Password?',
										block: false,
										type: 'link',
										onClick: () => {
											setForgotPasswordModal(true);
										},
									},
								},
								{
									name: 'rememberMe',
									type: 'checkbox',
									width: '1/2',
									config: {
										children: 'Ingatkan Saya',
										style: {
											display: 'flex',
											justifyContent: 'flex-end',
										},
									},
								},
								{
									type: 'button',
									config: {
										loading: isLoading,
										type: 'primary',
										htmlType: 'submit',
										children: 'Login',
										size: 'large',
										shape: 'round',
									},
								},
							]}
						/>
					</Col>
				</Row>
			</div>

			<ForgotPasswordModal {...forgotPasswordModal} />
		</Fragment>
	);
};

export default LoginPage;
